import Highcharts from "highcharts";
import { toSafeNumber } from "../data/transformers";
import { QUADRANT_SCATTER_CONFIG } from "src/config/charts/quadrantScatterChartConfig";
import { getTierStyle } from "src/charts/types";
import type { QuadrantPractice } from "../types";

const TIER_ORDER = ["Performing", "Steady", "Priority Focus", "External"] as const;

// Thresholds drawn as quadrant lines when the widget passes them via data._thresholds.
export type QuadrantThresholds = { pa?: number; abandon?: number };

const parsePercent = (val: string | undefined): number => {
  if (!val) return 0;
  return parseFloat(val.replace("%", "")) || 0;
};

const fadeColor = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Marker area encodes case volume (sqrt scale so area ~ pts).
const MIN_RADIUS = 5;
const MAX_RADIUS = 18;

// When a practice is selected, everything else drops to this opacity.
const DIMMED_ALPHA = 0.14;

export function renderQuadrantScatter(
  data: unknown,
  _spec: unknown,
  _title: string,
  onPointClick?: (point: unknown) => void,
): Highcharts.Options {
  const practices: QuadrantPractice[] = Array.isArray(data)
    ? data
    : ((data as any)?.practices ?? []);
  const selectedId: number | string | null = Array.isArray(data)
    ? null
    : ((data as any)?._selectedId ?? null);
  const thresholds: QuadrantThresholds = Array.isArray(data)
    ? {}
    : ((data as any)?._thresholds ?? {});
  const hiddenTiers: string[] = Array.isArray(data)
    ? []
    : ((data as any)?._hiddenTiers ?? []);
  // When provided, legend clicks report the toggled tier to the widget instead
  // of only hiding the series locally — the widget re-renders every connected
  // chart (scatter, map, list) from the same hidden-tier state.
  const onTierToggle: ((tier: string) => void) | undefined = Array.isArray(data)
    ? undefined
    : ((data as any)?._onTierToggle ?? undefined);

  const typedOnPointClick = onPointClick as
    | ((practice: QuadrantPractice | null) => void)
    | undefined;
  const hasDrilldown = !!typedOnPointClick;
  const hasSelection = selectedId !== null && selectedId !== undefined;

  const maxPts = Math.max(
    ...practices.map((p) => toSafeNumber(p.pts)),
    1,
  );
  const radiusFor = (pts: number): number =>
    Math.round(
      MIN_RADIUS + Math.sqrt(pts / maxPts) * (MAX_RADIUS - MIN_RADIUS),
    );

  const isSelectedPractice = (p: QuadrantPractice): boolean =>
    hasSelection &&
    (p.practice_id !== undefined
      ? p.practice_id === selectedId
      : p.practice === selectedId);

  const series: Highcharts.SeriesScatterOptions[] = TIER_ORDER.map((tier) => {
    const style = getTierStyle(tier);
    return {
      type: "scatter" as const,
      name: tier,
      color: style.dot,
      visible: !hiddenTiers.includes(tier),
      marker: { symbol: "circle" },
      data: practices
        .filter((p) => p.tier === tier)
        .map((p) => {
          const pts = toSafeNumber(p.pts);
          const radius = radiusFor(pts);
          const isSelected = isSelectedPractice(p);
          const isDimmed = hasSelection && !isSelected;
          return {
            x: parsePercent(p.abandon_percentage),
            y: parsePercent(p.pa_percentage),
            marker: {
              radius: isSelected ? radius + 3 : radius,
              // 2px surface ring separates overlapping bubbles; the selected
              // point gets a stronger ring so it reads as "picked", not "hovered".
              fillColor: isDimmed
                ? fadeColor(style.dot, DIMMED_ALPHA)
                : fadeColor(style.dot, 0.85),
              lineColor: isSelected
                ? "#FFFFFF"
                : isDimmed
                  ? "rgba(255,255,255,0.4)"
                  : "rgba(255,255,255,0.9)",
              lineWidth: isSelected ? 2.5 : 1,
              states: {
                hover: {
                  radiusPlus: 2,
                  lineWidth: 2,
                  lineColor: "#FFFFFF",
                },
              },
            },
            custom: {
              practice: p.practice ?? "",
              pts,
              pa_cases: toSafeNumber(p.pa_cases),
              pa_approved: toSafeNumber(p.pa_approved_cases),
              pa_percentage: p.pa_percentage ?? "0.0%",
              abandon_percentage: p.abandon_percentage ?? "0.0%",
              tier: p.tier ?? "",
              color: style.dot,
              isSelected,
              _raw: p,
            },
          };
        }),
    };
  });

  const thresholdLineStyle = {
    color: "#94A3B8",
    dashStyle: "Dash" as Highcharts.DashStyleValue,
    width: 1,
    zIndex: 1,
  };
  const thresholdLabelStyle = {
    color: "#64748B",
    fontSize: "10px",
  };

  return {
    ...QUADRANT_SCATTER_CONFIG,
    chart: {
      ...QUADRANT_SCATTER_CONFIG.chart,
      type: "scatter",
      marginTop: 24,
      height: 550,
      // Selection changes animate smoothly (marker radius/color updates).
      animation: { duration: 200 },
      events: {
        // Clicking empty plot area clears the selection.
        click: function () {
          if (typedOnPointClick) typedOnPointClick(null);
        },
      },
    },
    // Axis extremes are padded past 0–100 so markers at the domain edges sit
    // fully inside the plot area — Highcharts ignores pointer events outside
    // it, which made edge bubbles un-hoverable/un-clickable from one side.
    // Ticks stay pinned to 0–100 so the padding is invisible in the labels.
    xAxis: {
      title: { text: "PA Abandonment %", style: { fontSize: "11px" } },
      min: -4,
      max: 104,
      tickPositions: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      gridLineWidth: 1,
      gridLineColor: "#EEF1F5",
      startOnTick: false,
      endOnTick: false,
      labels: {
        style: { fontSize: "11px" },
        formatter: function (
          this: Highcharts.AxisLabelsFormatterContextObject,
        ) {
          return `${this.value}%`;
        },
      },
      plotLines:
        thresholds.abandon !== undefined
          ? [
              {
                ...thresholdLineStyle,
                value: thresholds.abandon,
                label: {
                  text: `Abandon ${thresholds.abandon}%`,
                  rotation: 0,
                  y: 12,
                  x: 4,
                  style: thresholdLabelStyle,
                },
              },
            ]
          : [],
    },
    yAxis: {
      title: { text: "PA Approval %", style: { fontSize: "11px" } },
      min: -8,
      max: 108,
      tickPositions: [0, 25, 50, 75, 100],
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 1,
      gridLineColor: "#EEF1F5",
      labels: {
        style: { fontSize: "11px" },
        formatter: function (
          this: Highcharts.AxisLabelsFormatterContextObject,
        ) {
          return `${this.value}%`;
        },
      },
      plotLines:
        thresholds.pa !== undefined
          ? [
              {
                ...thresholdLineStyle,
                value: thresholds.pa,
                label: {
                  text: `PA target ${thresholds.pa}%`,
                  align: "right",
                  x: -4,
                  y: -6,
                  style: thresholdLabelStyle,
                },
              },
            ]
          : [],
    },
    tooltip: {
      useHTML: true,
      formatter: function (this: any) {
        const c = this.point?.options?.custom ?? {};
        const s = getTierStyle(c.tier);
        const drillHint = hasDrilldown
          ? `<div style="margin-top:6px;font-size:10px;color:#94a3b8;">${
              c.isSelected ? "Click again to clear selection" : "Click to see prescribers"
            }</div>`
          : "";
        return `<div style="padding:6px 8px;min-width:210px;max-width:280px;">
          <div style="font-size:12px;font-weight:700;margin-bottom:4px;word-break:break-word;">${c.practice}</div>
          <div style="display:flex;align-items:center;gap:5px;margin-bottom:6px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${s.dot}"></span>
            <span style="font-size:11px;color:${s.text};background:${s.bg};border:1px solid ${s.border};border-radius:10px;padding:0 6px">${c.tier}</span>
          </div>
          <table style="font-size:11px;border-collapse:collapse;width:100%">
            <tr><td style="color:#64748B;padding-right:10px">Total Cases</td><td style="font-weight:600">${c.pts}</td></tr>
            <tr><td style="color:#64748B;padding-right:10px">PA Cases</td><td style="font-weight:600">${c.pa_cases}</td></tr>
            <tr><td style="color:#64748B;padding-right:10px">PA Approved</td><td style="font-weight:600;color:#15803D">${c.pa_percentage}</td></tr>
            <tr><td style="color:#64748B;padding-right:10px">Abandoned</td><td style="font-weight:600;color:#DC2626">${c.abandon_percentage}</td></tr>
          </table>
          ${drillHint}
        </div>`;
      },
    },
    plotOptions: {
      scatter: {
        cursor: hasDrilldown ? "pointer" : "default",
        animation: false,
        stickyTracking: false,
        // Selection dimming is rendered declaratively from _selectedId above.
        // Disable Highcharts' own hover-driven series dimming so the two
        // never fight (that fight was the old "some blur, some don't" bug).
        states: {
          hover: { enabled: true },
          inactive: { enabled: false },
        },
        events: onTierToggle
          ? {
              legendItemClick: function (this: any) {
                onTierToggle(this.name);
                return false; // widget state drives visibility, not Highcharts
              },
            }
          : {},
        dataLabels: { enabled: false },
        point: {
          events: hasDrilldown
            ? {
                click: function (this: any) {
                  const raw = this.options?.custom?._raw as
                    | QuadrantPractice
                    | undefined;
                  if (raw && typedOnPointClick) typedOnPointClick(raw);
                },
              }
            : {},
        },
      },
    },
    series,
  };
}
